// Named-step pipeline replacing inline handleSurveySubmit (F5).
// Pipeline: validate -> persistSurvey -> deleteRemovedQuestions ->
// upsertQuestions (per-question error capture) -> finalize.
// F21: partial-success state when upsert fails after persist succeeds;
// retryFailedUpserts re-runs only the failed items.

import { useCallback, useRef, useState } from "react";

import { fetcherFull, fetcherSimple } from "utils/api";
import { PAYMENT_REQUIRED } from "store/auth-status";
import {
    SurveySubmitError,
    VALIDATION_FAILED,
    SURVEY_NOT_EDITABLE,
    SURVEY_NOT_EDITABLE_MESSAGE,
    SURVEY_PERSIST_FAILED,
    QUESTION_PERSIST_FAILED,
} from "utils/survey-submit-error";
import {
    isValidDatetimeInput,
    isEndDateBeforeStartDate,
    localDatetimeInputToUtc,
    nowUtcDatetimeInput,
} from "../date-helpers";
import {
    isFinishedOrActive,
    isLivEditable,
    shouldDeleteStartDateOnSave,
} from "../lifecycle-visibility";
import { reindexAfterDelete } from "../handlers/question-handlers";

// Throw on responses that the fetcher treats as 2xx-success but whose
// body signals an application-level error. The legacy backend contract
// returns 2xx with `{ error: ... }` bodies for some validation cases;
// without this guard, Promise.allSettled would capture them as
// fulfilled and the failure-collection logic would miss them.
function throwOnBodyError(response) {
    if (response && response.error) {
        const message = response.message
            || (typeof response.error === "string"
                ? response.error
                : "Server error");
        throw new Error(message);
    }
    return response;
}

// Build the PUT /v2/survey/:id payload from the form values. Mirrors the
// pre-processing that lived inline in handleSurveySubmit: convert local
// datetime inputs to UTC, drop empty optional URLs, normalise the
// anonymous flag, and strip start_date on paused surveys.
function buildPayload(values, surveyState, instantActive) {
    const payload = { ...values };
    if (isValidDatetimeInput(payload.end_date)) {
        payload.end_date = localDatetimeInputToUtc(payload.end_date);
    }
    if (instantActive) {
        payload.start_date = nowUtcDatetimeInput();
    }
    if (!instantActive && isValidDatetimeInput(payload.start_date)) {
        payload.start_date = localDatetimeInputToUtc(payload.start_date);
    }
    if (!payload.survey_website) delete payload.survey_website;
    if (!payload.policy_url) delete payload.policy_url;
    if (shouldDeleteStartDateOnSave(surveyState)) delete payload.start_date;
    if (!payload.start_date) delete payload.start_date;
    if (!payload.end_date) delete payload.end_date;
    payload.is_allow_anonymous = Boolean(payload.is_allow_anonymous);
    return payload;
}

// Step 1: throw VALIDATION_FAILED for any state-mismatch or invalid input
// that should short-circuit the save without hitting the backend.
//
// Active/finished surveys are read-only EXCEPT for the use_comprehend
// flag, which can still be toggled. When only that flag changed, the
// caller (submit pipeline) skips the rest of validate() and persists a
// minimal { use_comprehend } payload. We surface that intent via the
// thrown error's `useComprehendOnly` field so the caller can branch
// without re-deriving the condition.
function validate(survey, values, instantActive) {
    if (!survey) {
        throw new SurveySubmitError(
            "Survey not loaded",
            VALIDATION_FAILED,
        );
    }
    if (isFinishedOrActive(survey.state)) {
        // Normalise both sides: an unset flag (undefined) on either side
        // must compare equal to an explicit false on the other. Without
        // this, `undefined !== Boolean(undefined)` would always be true
        // and trigger the use_comprehend-only PUT path even when the
        // form value matches the persisted survey value.
        const useComprehendChanged =
            Boolean(values.use_comprehend)
            !== Boolean(survey.use_comprehend);
        if (useComprehendChanged) {
            // Allowed: minimal payload path. Throwing lets the submit
            // pipeline short-circuit the rest of validation and skip
            // the full-payload build. The caller inspects the thrown
            // error's `useComprehendOnly` field to detect this case.
            throw new SurveySubmitError(
                "use_comprehend only",
                VALIDATION_FAILED,
                { useComprehendOnly: true },
            );
        }
        throw new SurveySubmitError(
            `Survey cannot be edited in ${survey.state} state.`,
            VALIDATION_FAILED,
        );
    }
    if (!isLivEditable(survey.state)) {
        throw new SurveySubmitError(
            "Survey has already been started and can no longer be edited! " +
                "You can pause the survey for more features.",
            VALIDATION_FAILED,
        );
    }
    const startDate = instantActive
        ? nowUtcDatetimeInput()
        : isValidDatetimeInput(values.start_date)
            ? localDatetimeInputToUtc(values.start_date)
            : values.start_date;
    const endDate = isValidDatetimeInput(values.end_date)
        ? localDatetimeInputToUtc(values.end_date)
        : values.end_date;
    if (!instantActive && isEndDateBeforeStartDate(startDate, endDate)) {
        throw new SurveySubmitError(
            "Start Date cannot be after the End Date!",
            VALIDATION_FAILED,
        );
    }
}

// Classify a backend error code from the survey PUT into a SurveySubmitError
// with a caller-facing code and friendly message. `code` is `ApiError.code`
// (non-2xx throw path) or `response.error` (2xx-with-error-body legacy path);
// in both cases it holds the backend's `error` field verbatim.
//
// Known codes get a typed SurveySubmitError so callers can branch. Unknown
// codes fall through to SURVEY_PERSIST_FAILED with the raw message preserved.
function classifyPersistError(code, rawMessage) {
    if (code === PAYMENT_REQUIRED) {
        return new SurveySubmitError(rawMessage, PAYMENT_REQUIRED);
    }
    if (code === SURVEY_NOT_EDITABLE) {
        return new SurveySubmitError(
            SURVEY_NOT_EDITABLE_MESSAGE,
            SURVEY_NOT_EDITABLE,
        );
    }
    return new SurveySubmitError(
        rawMessage || "Survey save failed",
        SURVEY_PERSIST_FAILED,
    );
}

// Step 2: PUT /v2/survey/:id. fetcherFull throws ApiError on non-2xx, but
// the original code also tolerated 2xx responses with `{ error }` bodies
// (rare legacy contract). Both paths are classified via classifyPersistError,
// which inspects the structured `code` field rather than substring-matching
// the message.
async function persistSurvey(surveyId, payload) {
    let response;
    try {
        response = await fetcherFull(
            `/v2/survey/${surveyId}`,
            "PUT",
            payload,
        );
    } catch (err) {
        throw classifyPersistError(err?.code, err?.message);
    }
    if (!response || !response.name) {
        throw classifyPersistError(
            response?.error,
            response?.error || "Survey save failed",
        );
    }
    return response;
}

// Step 3: await Promise.all of DELETE /v2/survey/:id/question/:qid (F4).
// Returns { successes, failures } rather than throwing on partial failure;
// callers decide whether to surface as partial-success or hard error.
async function deleteRemovedQuestions(surveyId, removedIds) {
    if (!removedIds || removedIds.length === 0) {
        return { successes: [], failures: [] };
    }
    const results = await Promise.allSettled(
        removedIds.map((questionId) =>
            fetcherSimple(
                `/v2/survey/${surveyId}/question/${questionId}`,
                "DELETE",
            ).then((response) => {
                throwOnBodyError(response);
                return questionId;
            }),
        ),
    );
    const successes = [];
    const failures = [];
    results.forEach((result, index) => {
        const questionId = removedIds[index];
        if (result.status === "fulfilled") {
            successes.push(questionId);
        } else {
            failures.push({ questionId, error: result.reason });
        }
    });
    return { successes, failures };
}

// Step 4: Promise.all of POST (new, no nanoid) and PUT (existing) calls.
// Same shape as deleteRemovedQuestions: per-question failure capture so
// partial-success state can be surfaced (F21).
async function upsertQuestions(surveyId, questions) {
    if (!questions || questions.length === 0) {
        return { successes: [], failures: [], total: 0 };
    }
    const results = await Promise.allSettled(
        questions.map(async (question) => {
            const isNew = question.nanoid === undefined;
            const method = isNew ? "POST" : "PUT";
            const path = isNew
                ? `/v2/survey/${surveyId}/question`
                : `/v2/survey/${surveyId}/question/${question.id}`;
            const response = await fetcherFull(path, method, question);
            throwOnBodyError(response);
            return question;
        }),
    );
    const successes = [];
    const failures = [];
    results.forEach((result, index) => {
        if (result.status === "fulfilled") {
            successes.push(questions[index]);
        } else {
            failures.push({ question: questions[index], error: result.reason });
        }
    });
    return { successes, failures, total: questions.length };
}

export function useSurveySubmit({
    survey,
    surveyId,
    surveyMutate,
    surveyQuestionsMutate,
    invalidateSurveyCaches,
    instantActive,
}) {
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState(null);
    const [partialSuccess, setPartialSuccess] = useState(null);
    // Mirror partialSuccess in a ref so retryFailedUpserts can read the
    // current value without closing over the state directly (which would
    // churn the callback identity). React 18's batching means setState
    // updater functions are queued, not called synchronously, so the
    // local-variable-from-updater pattern cannot be used to decide
    // whether to transition to "partial-success" vs "success".
    const partialSuccessRef = useRef(null);
    const submittingRef = useRef(false);

    // Keep the ref in sync after every render. This runs before any
    // event handler / effect / retry callback reads it.
    partialSuccessRef.current = partialSuccess;

    const submit = useCallback(
        async (
            values,
            { questionsToDelete = [], onQuestionsDeleted } = {},
        ) => {
            if (submittingRef.current) {
                return { status: "idle", reason: "already-submitting" };
            }
            submittingRef.current = true;
            setStatus("validating");
            setError(null);
            setPartialSuccess(null);

            try {
                try {
                    validate(survey, values, instantActive);
                } catch (validateErr) {
                    if (
                        validateErr instanceof SurveySubmitError &&
                        validateErr.useComprehendOnly
                    ) {
                        // Active/finished + only use_comprehend changed:
                        // PUT the minimal payload, skip question upserts
                        // and deletes (the rest of the survey is read-only),
                        // then surface success.
                        setStatus("submitting");
                        await persistSurvey(surveyId, {
                            use_comprehend: values.use_comprehend,
                        });
                        if (typeof surveyMutate === "function") surveyMutate();
                        if (typeof invalidateSurveyCaches === "function") {
                            invalidateSurveyCaches();
                        }
                        setStatus("success");
                        return { status: "success" };
                    }
                    throw validateErr;
                }

                setStatus("submitting");
                const payload = buildPayload(
                    values,
                    survey.state,
                    instantActive,
                );
                await persistSurvey(surveyId, payload);

                const deleteResults = await deleteRemovedQuestions(
                    surveyId,
                    questionsToDelete,
                );
                if (
                    onQuestionsDeleted &&
                    deleteResults.failures.length === 0
                ) {
                    onQuestionsDeleted();
                }

                const reindexed = reindexAfterDelete(
                    values.questions || [],
                );
                const upsertResults = await upsertQuestions(
                    surveyId,
                    reindexed,
                );

                // Cache invalidation always runs after the survey PUT
                // succeeded, even when question upserts or deletes partially
                // failed; the survey record itself has been persisted.
                if (typeof surveyMutate === "function") surveyMutate();
                if (typeof surveyQuestionsMutate === "function") {
                    surveyQuestionsMutate();
                }
                if (typeof invalidateSurveyCaches === "function") {
                    invalidateSurveyCaches();
                }

                // Partial-success covers three combinations:
                //   - deletes failed + upserts ok
                //   - deletes ok     + upserts failed
                //   - both failed
                // Any per-question/per-delete failure must be surfaced so the
                // caller can render a toast that names what failed; silently
                // transitioning to "success" would drop the failure signal.
                const hasDeleteFailures = deleteResults.failures.length > 0;
                const hasUpsertFailures = upsertResults.failures.length > 0;
                if (hasDeleteFailures || hasUpsertFailures) {
                    const partial = {
                        survey: true,
                        deletedCount: deleteResults.successes.length,
                        deleteFailures: deleteResults.failures,
                        upsertFailures: upsertResults.failures,
                        upsertTotal: upsertResults.total,
                    };
                    setPartialSuccess(partial);
                    setStatus("partial-success");
                    return { status: "partial-success", partialSuccess: partial };
                }

                setStatus("success");
                return { status: "success" };
            } catch (err) {
                const typedError =
                    err instanceof SurveySubmitError
                        ? err
                        : new SurveySubmitError(
                            err?.message || "Unknown error",
                            SURVEY_PERSIST_FAILED,
                        );
                setError(typedError);
                setStatus("error");
                return { status: "error", error: typedError };
            } finally {
                submittingRef.current = false;
            }
        },
        [
            survey,
            surveyId,
            surveyMutate,
            surveyQuestionsMutate,
            invalidateSurveyCaches,
            instantActive,
        ],
    );

    // F21 retry path: re-attempt only the question upserts that failed.
    // The survey PUT already succeeded; we don't need to re-validate or
    // re-persist it. Caller passes the partialSuccess.upsertFailures array
    // captured from the prior submit attempt.
    //
    // The callback intentionally does NOT close over `partialSuccess`:
    // reading it from the closure would (a) churn the callback identity on
    // every state change and (b) risk a stale view if rapid retries
    // outpace React's commit. State reads happen via the functional
    // setPartialSuccess updater instead, and the hook's caller reads
    // `partialSuccess` from the hook return value rather than from this
    // function's return value.
    const retryFailedUpserts = useCallback(
        async (failures) => {
            if (!failures || failures.length === 0) {
                return { status: "idle", reason: "nothing-to-retry" };
            }
            // Mirror the submit() guard: a retry during an in-flight
            // submit or retry would race on the same SWR mutates and
            // produce out-of-order status transitions.
            if (submittingRef.current) {
                return { status: "idle", reason: "already-submitting" };
            }
            submittingRef.current = true;
            setStatus("submitting");
            setError(null);
            try {
                const retryItems = failures.map((f) => f.question);
                const results = await upsertQuestions(surveyId, retryItems);
                if (typeof surveyQuestionsMutate === "function") {
                    surveyQuestionsMutate();
                }
                if (typeof invalidateSurveyCaches === "function") {
                    invalidateSurveyCaches();
                }
                if (results.failures.length > 0) {
                    setPartialSuccess((prev) => ({
                        ...prev,
                        upsertFailures: results.failures,
                        upsertTotal: retryItems.length,
                    }));
                    setStatus("partial-success");
                    return { status: "partial-success" };
                }
                // Preserve deleteFailures from the original submit:
                // a successful upsert retry is not a full success if
                // deletes are still failing. We read the current
                // partialSuccess via the ref (set above) rather than
                // from a setPartialSuccess updater, since React 18
                // batches state updates and the updater would not run
                // synchronously enough to feed setStatus.
                const preservePartialSuccess =
                    (partialSuccessRef.current?.deleteFailures?.length || 0) > 0;
                if (preservePartialSuccess) {
                    setPartialSuccess({
                        deleteFailures: partialSuccessRef.current.deleteFailures,
                        deleteTotal: partialSuccessRef.current.deleteTotal || 0,
                        upsertFailures: [],
                        upsertTotal: 0,
                    });
                    setStatus("partial-success");
                    return { status: "partial-success" };
                }
                setPartialSuccess(null);
                setStatus("success");
                return { status: "success" };
            } catch (err) {
                const typedError =
                    err instanceof SurveySubmitError
                        ? err
                        : new SurveySubmitError(
                            err?.message || "Unknown retry error",
                            QUESTION_PERSIST_FAILED,
                            { failures },
                        );
                setError(typedError);
                setStatus("error");
                return { status: "error", error: typedError };
            } finally {
                submittingRef.current = false;
            }
        },
        [surveyId, surveyQuestionsMutate, invalidateSurveyCaches],
    );

    return {
        submit,
        status,
        error,
        partialSuccess,
        retryFailedUpserts,
    };
}
